import React, { PropsWithChildren } from 'react'
import AppBar from '../../components/AppBar'
import { Helmet } from 'react-helmet'
import styles from './styles.module.scss'
import classNames from 'classnames'
import { RainbowText } from '../../components/RainbowText'
import ss1 from './assets/ss1.png'
import ss2 from './assets/ss2.png'
import ss3 from './assets/ss3.png'
import { Screenshot } from '../../components/Screenshot'
import Fade from 'react-reveal/Fade'
import { ifElse } from '../../utilities'
import { AppFooter } from '../../components/AppFooter'
import List from '../../components/List'
import DownloadAndDiscordButtons from '../../components/DownloadAndDiscordButtons'

const DracoBot: React.FC<PropsWithChildren<{ reverse?: boolean; light?: boolean }>> = ({ reverse = true, light = true }) => {
  return <RainbowText light={light} reverse={reverse} text='Draco.Bot' />
}

type HomepageHeaderProps = {
  title: string
  subtitle: string
  reverse?: boolean
}

const HomepageHeader: React.FC<HomepageHeaderProps> = ({ title, subtitle, reverse }) => {
  return (
    <h1>
      <RainbowText text={title} reverse={reverse} />
      <small>{subtitle}</small>
    </h1>
  )
}

type HomepageSectionProps = PropsWithChildren<{
  title: string
  subtitle: string
  screenshot: string
  reverse?: boolean
}>

const HomepageSection: React.FC<HomepageSectionProps> = ({ title, subtitle, screenshot, reverse, children }) => {
  const ss = (
    <Fade key='ss' duration={1000}>
      <Screenshot className={styles.screenshot} src={[screenshot]} glowAmount='small' reverse={!reverse} />
    </Fade>
  )

  const content = (
    <Fade key='content' duration={300} distance='100px' right={!reverse} left={reverse}>
      <div className={styles.textContent}>
        <HomepageHeader {...{ title, subtitle, reverse }} />
        <div className={classNames('display-6', styles.subtitle)}>{children}</div>
      </div>
    </Fade>
  )

  return (
    <div className={classNames(styles.container, 'container')}>
      <div className={classNames(styles.content, styles.twoColumn)}>{ifElse(!reverse, [ss, content], [content, ss])}</div>
    </div>
  )
}

function ChatbotHome() {
  return (
    <div>
      <Helmet>
        <meta charSet='utf-8' />
        <title>Home | Draco.Bot</title>
      </Helmet>

      <AppBar />

      <div className={classNames(styles.container, styles.twoColumnContainer)}>
        <div className='container'>
          <div className={styles.content}>
            <div className={styles.textcontent}>
              <HomepageHeader title='Take Control of Your Stream' subtitle='without the headaches' reverse />
              <div className={classNames('display-6', styles.subtitle)}>
                <p>
                  <DracoBot /> replaces multiple Twitch tools with one reliable desktop app that automates chat, OBS, audio, and stream events - without cloud lag or flaky connections.
                </p>
                <List
                  className={styles.list}
                  more={false}
                  items={[
                    'Runs locally on your PC - no cloud delays',
                    'Automates chat, channel points, and Twitch events',
                    'Direct control over OBS, audio, and stream actions',
                    'Designed to stay stable during long streams',
                  ]}
                />
              </div>
            </div>

            <DownloadAndDiscordButtons />

            <div className={styles.screenshots}>
              <Screenshot src={[ss1, ss2, ss3]} />
            </div>
          </div>
        </div>
      </div>

      <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 50' className={styles.decoration}>
        <path fillOpacity='1' d='M0,50 C100,20 550,0 800,0 L 0,0' />
      </svg>

      <HomepageSection title='Stop' subtitle='juggling tools mid-stream' screenshot={ss1}>
        <p>Streaming often means stitching together multiple bots, browser tools, plugins, and workarounds - all reacting to the same events in slightly different ways.</p>
        <p>That setup works… until it doesn&apos;t. Lag, dropped events, broken automations, or one service going down can disrupt your stream at the worst possible time.</p>
        <p>
          <b>
            <DracoBot /> was built to solve that problem.
          </b>{' '}
          It brings viewer interaction, stream automation, and software control into one place - running locally, under your control.
        </p>
      </HomepageSection>

      <HomepageSection title='One tool.' subtitle='One setup. Fewer things to worry about.' screenshot={ss3} reverse>
        <p>
          <DracoBot /> helps your stream react automatically - without you micromanaging it.
        </p>
        <List
          className={styles.list}
          more={false}
          items={[
            'Respond to follows, subs, raids, ads, hype trains, bits, and channel point redemptions',
            'Create chat commands, greetings, and message reactions',
            'Control OBS scenes, sources, media, and audio directly',
            'Manage timers, countdowns, counters, and overlay output files',
            'Play music, sound effects, and text-to-speech on the audio device you choose',
          ]}
        />
      </HomepageSection>

      <HomepageSection title='Built for' subtitle='real streams, not just demos' screenshot={ss2}>
        <p>
          <DracoBot /> runs locally and is designed to stay open for long sessions.
        </p>
        <List className={styles.list} more={false} items={['No browser tabs to refresh', 'No cloud services adding latency', 'No fragile scripts needed for basic automation']} />
        <p>Reliability features include:</p>
        <List className={styles.list} more={false} items={['Automatic reconnects for Twitch and OBS', 'Action queuing to prevent overlaps and conflicts', 'Configuration backups and recovery']} />
      </HomepageSection>

      <HomepageSection title='Engage' subtitle='your viewers without micromanaging' screenshot={ss1} reverse>
        <p>Set it up once, then focus on your stream.</p>
        <List
          className={styles.list}
          more={false}
          items={[
            'Automatically greet viewers and welcome regulars',
            'React when someone mentions you in chat',
            'Trigger actions from channel point rewards',
            'Run raffles, timers, and interactive stream moments',
            'Reward VIPs, subs, and trusted viewers with special commands',
          ]}
        />
      </HomepageSection>

      <HomepageSection title='Automation' subtitle='that actually feels useful' screenshot={ss3}>
        <p>
          A few examples of what streamers use <DracoBot /> for:
        </p>
        <List
          className={styles.list}
          more={false}
          items={[
            'A channel point reward that plays a sound, switches an OBS scene, and updates an overlay',
            'A subathon-style countdown timer that extends automatically on subs or bits',
            'Music requests that play locally (no stream delay)',
            'VTuber model actions triggered by chat commands or stream events',
            'Auto-announcements for raids, Discord links, or stream milestones',
          ]}
        />
      </HomepageSection>

      <div className={classNames(styles.container, 'container')}>
        <div className={classNames(styles.content)}>
          <div className={styles.textContent}>
            <HomepageHeader title='Spend less time' subtitle='managing your stream' reverse />
            <div className={classNames('display-6', styles.subtitle)}>
              <p>
                <DracoBot /> is currently in public alpha and actively improved based on real streamer feedback.
              </p>
              <p>If you want a bot that makes your stream easier to run - without relying on fragile cloud services - you&apos;re in the right place.</p>
            </div>
            <div style={{ marginTop: 24 }}>
              <DownloadAndDiscordButtons />
            </div>
          </div>
        </div>
      </div>

      <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 50' className={classNames(styles.decoration, styles.flip)}>
        <path fillOpacity='1' d='M0,50 C100,20 550,0 800,0 L 0,0' />
      </svg>

      <div className={styles.footer}>
        <AppFooter />
      </div>
    </div>
  )
}

export default ChatbotHome
